/** @format */

import moment from "moment";
import { IContributionStatus } from "../types/contributionType";

export type ContributionBlockReason =
	| "not_member"
	| "no_cycle"
	| "cycle_not_started"
	| "cycle_ended"
	| "mid_cycle_joiner"
	| "not_contribution_day"
	| "already_contributed";

export interface ContributionAvailability {
	canContribute: boolean;
	reason: ContributionBlockReason | null;
	message: string;
}

interface AvailabilityInput {
	circle: any;
	currentCycle: any;
	status?: IContributionStatus;
	currentUserId: string;
	justContributed?: boolean;
	now?: moment.Moment;
}

const blocked = (
	reason: ContributionBlockReason,
	message: string,
): ContributionAvailability => ({ canContribute: false, reason, message });

/**
 * Whether the member can make today's contribution to a circle, and if not why.
 * Days are evaluated in the user's local timezone; cycle bounds in UTC.
 */
export const getContributionAvailability = ({
	circle,
	currentCycle,
	status,
	currentUserId,
	justContributed = false,
	now = moment(),
}: AvailabilityInput): ContributionAvailability => {
	const isMember = circle?.members?.some(
		(member: any) =>
			member?.memberId === currentUserId ||
			member?.memberId?._id === currentUserId ||
			member?._id === currentUserId,
	);

	if (!isMember) {
		return blocked("not_member", "You are not a member of this circle");
	}

	if (!currentCycle) {
		return blocked("no_cycle", "No Active Contribution Cycle");
	}

	const nowUTC = now.clone().utc();
	const cycleStartDateUtc = moment.utc(currentCycle.startDate).startOf("day");
	const cycleEndDateUtc =
		currentCycle.endDate ?
			moment.utc(currentCycle.endDate).add(1, "day").startOf("day")
		:	moment.utc(currentCycle.payOutDate);

	if (nowUTC.isBefore(cycleStartDateUtc)) {
		return blocked(
			"cycle_not_started",
			`Cycle hasn't started yet. Contributions allowed starting from: ${cycleStartDateUtc.format(
				"YYYY-MM-DD HH:mm",
			)}`,
		);
	}

	if (nowUTC.isSameOrAfter(cycleEndDateUtc)) {
		const lastAllowed = cycleEndDateUtc.clone().subtract(1, "minute");
		return blocked(
			"cycle_ended",
			`Cycle has ended. Last contribution window (UTC): ${lastAllowed.format(
				"YYYY-MM-DD HH:mm",
			)}`,
		);
	}

	if (status?.isMidCycleJoiner === true && status?.canContributeYet === false) {
		const eligibleDate =
			status.eligibleToContributeFrom ?
				moment(status.eligibleToContributeFrom).format("MMMM D, YYYY")
			:	"a future date";
		return blocked(
			"mid_cycle_joiner",
			`You joined this circle mid-cycle and can start contributing from ${eligibleDate}`,
		);
	}

	const todayWeekdayLocal = now.format("dddd");
	const allowedDays: string[] = circle?.configuration?.contributionDays || [];

	if (allowedDays.length && !allowedDays.includes(todayWeekdayLocal)) {
		return blocked("not_contribution_day", "Not a contribution Day");
	}

	if (status?.hasContributedToday === true || justContributed) {
		return blocked("already_contributed", "You have already contributed today");
	}

	return { canContribute: true, reason: null, message: "" };
};

/**
 * Next local date (after today) that falls on one of the contribution days,
 * or null if there are none.
 */
export const getNextContributionDate = (
	contributionDays: string[] | undefined,
	now: moment.Moment = moment(),
): moment.Moment | null => {
	if (!contributionDays?.length) return null;
	for (let offset = 1; offset <= 7; offset++) {
		const candidate = now.clone().add(offset, "day");
		if (contributionDays.includes(candidate.format("dddd"))) {
			return candidate.startOf("day");
		}
	}
	return null;
};
